import { memo } from 'react'
import type { AgentState, AgentStep } from '#/index'
import { STEP_LABELS } from '#/index'
import './index.scss'

interface StepNavigationProps {
  agents: AgentState[]
  activeViewStep: AgentStep
  onStepClick: (step: AgentStep) => void
}

/** 按钮状态 → CSS 类名映射 */
const STATUS_CLASS_MAP: Record<string, string> = {
  running: 'step-running',
  done: 'step-done',
  error: 'step-error',
}

/** 按钮可点击的状态：已完成/运行中/失败时可点击切换查看 */
const CLICKABLE_STATUSES = new Set(['done', 'running', 'error'])

/**
 * 计算连接线两端的 CSS 类名
 * - 两端都完成 → 全蓝
 * - 仅一端完成 → 完成端蓝、未完成端灰
 * - 有错误端 → 错误端红
 */
function getConnectorClass(leftStatus: string, rightStatus: string): string {
  const isDone = (s: string) => s === 'done'
  const isError = (s: string) => s === 'error'

  if (isError(leftStatus) || isError(rightStatus)) {
    const classes = ['step-connector-line']
    if (isError(leftStatus)) classes.push('connector-left-error')
    else if (isDone(leftStatus)) classes.push('connector-left-done')
    if (isError(rightStatus)) classes.push('connector-right-error')
    else if (isDone(rightStatus)) classes.push('connector-right-done')
    return classes.join(' ')
  }

  if (isDone(leftStatus) && isDone(rightStatus)) {
    return 'step-connector-line connector-both-done'
  }
  if (isDone(leftStatus) && !isDone(rightStatus)) {
    return 'step-connector-line connector-left-done'
  }
  return 'step-connector-line'
}

/** 步骤导航组件 — 三个步骤按钮横向排列，按钮间用连接线串联 */
function StepNavigation({ agents, activeViewStep, onStepClick }: StepNavigationProps) {
  const isClickable = (status: string) => CLICKABLE_STATUSES.has(status)

  return (
    <div className="step-nav">
      {STEP_LABELS.map((label, index) => {
        const agent = agents[index]
        const status = agent?.status || 'pending'
        const isActive = index === activeViewStep

        const statusClass = STATUS_CLASS_MAP[status] || ''

        return (
          <span key={index} className="step-nav-item-wrapper">
            {index > 0 && (
              <span
                className={getConnectorClass(
                  agents[index - 1]?.status || 'pending',
                  agents[index]?.status || 'pending'
                )}
              />
            )}
            <button
              className={`step-btn ${statusClass} ${isActive ? 'step-active' : ''}`}
              disabled={!isClickable(status)}
              onClick={() => isClickable(status) && onStepClick(index as AgentStep)}
            >
              <span className="step-num">{index + 1}</span>
              <span className="step-label-text">{label}</span>
            </button>
          </span>
        )
      })}
    </div>
  )
}

/** 仅比较 status 和 activeViewStep，忽略 content 变化（流式期间每秒数十次） */
export default memo(
  StepNavigation,
  (prev, next) =>
    prev.activeViewStep === next.activeViewStep &&
    prev.agents[0].status === next.agents[0].status &&
    prev.agents[1].status === next.agents[1].status &&
    prev.agents[2].status === next.agents[2].status
)
